import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Brain, Clock, Layers, AlertTriangle, CheckCircle, RefreshCw, Share2, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LabNavigation from '@/components/LabNavigation';

// ============================================================================
// FONT LOADER
// ============================================================================
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500&family=Source+Serif+4:wght@400;500;600&display=swap');
  `}</style>
);

// ============================================================================
// SOUND ENGINE
// ============================================================================
class ResidueSoundEngine {
  private audioContext: AudioContext | null = null;
  private isEnabled: boolean = true;

  initialize() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  // Drain sound - descending pitch
  playDrain() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.3);

    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  }

  // Recovery chime - ascending notes
  playRecovery() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const notes = [392, 523.25, 659.25]; // G4, C5, E5
    notes.forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime);

        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      }, i * 120);
    });
  }

  // Alert sound for critical bandwidth
  playAlert() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    [0, 200].forEach((delay) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'square';
        osc.frequency.setValueAtTime(440, ctx.currentTime);

        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
      }, delay);
    });
  }

  // Click feedback
  playClick() {
    if (!this.isEnabled) return;
    const ctx = this.initialize();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);

    gain.gain.setValueAtTime(0.05, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.05);
  }
}

const soundEngine = new ResidueSoundEngine();

// ============================================================================
// TYPES
// ============================================================================
interface DiagnosticAnswers {
  taskSwitches: number;
  longestFocus: string;
  openTabs: number;
}

interface ResidueItem {
  source: string;
  percentage: number;
  time: string;
  description: string;
}

// ============================================================================
// BANDWIDTH BAR COMPONENT
// ============================================================================
interface BandwidthBarProps {
  percentage: number;
  showFragments?: boolean;
  residueItems?: ResidueItem[];
  animate?: boolean;
}

const BandwidthBar: React.FC<BandwidthBarProps> = ({
  percentage,
  showFragments = false,
  residueItems = [],
  animate = true
}) => {
  const getColor = (pct: number) => {
    if (pct >= 70) return '#22c55e';
    if (pct >= 40) return '#f59e0b';
    return '#ef4444';
  };

  const color = getColor(percentage);

  return (
    <div className="w-full">
      <div className="relative h-8 rounded-lg overflow-hidden bg-gray-800/50 border border-gray-700">
        {/* Available bandwidth */}
        <motion.div
          className="absolute left-0 top-0 bottom-0 rounded-lg"
          style={{ background: color }}
          initial={animate ? { width: '100%' } : { width: `${percentage}%` }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />

        {/* Fragments showing residue */}
        {showFragments && residueItems.map((item, index) => {
          const startPos = percentage + residueItems.slice(0, index).reduce((sum, i) => sum + i.percentage, 0);
          return (
            <motion.div
              key={item.source}
              className="absolute top-0 bottom-0"
              style={{
                left: `${startPos}%`,
                width: `${item.percentage}%`,
                background: `rgba(156, 163, 175, ${0.3 + index * 0.1})`,
                borderLeft: '1px solid rgba(255,255,255,0.1)'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.2 }}
            />
          );
        })}

        {/* Percentage label */}
        <div
          className="absolute inset-0 flex items-center justify-center text-sm font-bold"
          style={{
            color: percentage > 50 ? '#000' : '#fff',
            fontFamily: 'JetBrains Mono, monospace'
          }}
        >
          {percentage}%
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUESTION COMPONENTS
// ============================================================================
interface QuestionProps {
  question: string;
  subtext: string;
  children: React.ReactNode;
  index: number;
}

const Question: React.FC<QuestionProps> = ({ question, subtext, children, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.2 }}
    className="mb-12"
  >
    <div className="mb-6">
      <span
        className="text-sm text-emerald-400/60 uppercase tracking-wider mb-2 block"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        Question {index + 1}
      </span>
      <h3
        className="text-xl md:text-2xl font-semibold text-gray-100 mb-2"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {question}
      </h3>
      <p className="text-gray-500 text-sm">{subtext}</p>
    </div>
    {children}
  </motion.div>
);

// ============================================================================
// SLIDER COMPONENT
// ============================================================================
interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  labels: string[];
}

const Slider: React.FC<SliderProps> = ({ value, onChange, min, max, labels }) => {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="relative">
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          soundEngine.playClick();
          onChange(parseInt(e.target.value));
        }}
        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
        style={{
          background: `linear-gradient(to right, #22c55e ${percentage}%, #374151 ${percentage}%)`
        }}
      />
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        {labels.map((label, i) => (
          <span key={i}>{label}</span>
        ))}
      </div>
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .slider-thumb::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          border: 2px solid #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// OPTION SELECTOR
// ============================================================================
interface OptionSelectorProps {
  options: { value: string; label: string; description: string }[];
  selected: string;
  onChange: (value: string) => void;
}

const OptionSelector: React.FC<OptionSelectorProps> = ({ options, selected, onChange }) => (
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
    {options.map((option) => (
      <button
        key={option.value}
        onClick={() => {
          soundEngine.playClick();
          onChange(option.value);
        }}
        className={`p-4 rounded-xl text-center transition-all ${
          selected === option.value
            ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
            : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:bg-gray-700/50'
        } border`}
      >
        <div className="text-lg font-semibold mb-1">{option.label}</div>
        <div className="text-xs opacity-60">{option.description}</div>
      </button>
    ))}
  </div>
);

// ============================================================================
// DIAGNOSTIC CALCULATOR
// ============================================================================
const DiagnosticCalculator: React.FC<{
  onComplete: (answers: DiagnosticAnswers, bandwidth: number, residue: ResidueItem[]) => void;
}> = ({ onComplete }) => {
  const [taskSwitches, setTaskSwitches] = useState(5);
  const [longestFocus, setLongestFocus] = useState('30-60');
  const [openTabs, setOpenTabs] = useState(10);
  const [isCalculating, setIsCalculating] = useState(false);

  const focusOptions = [
    { value: '<15', label: '<15min', description: 'Very fragmented' },
    { value: '15-30', label: '15-30min', description: 'Fragmented' },
    { value: '30-60', label: '30-60min', description: 'Moderate' },
    { value: '60-90', label: '60-90min', description: 'Good' },
    { value: '90+', label: '90min+', description: 'Flow state' },
  ];

  const calculateBandwidth = () => {
    setIsCalculating(true);
    soundEngine.playDrain();

    setTimeout(() => {
      // Calculate bandwidth based on inputs
      let bandwidth = 100;
      const residue: ResidueItem[] = [];

      // Task switches impact (each switch costs ~5-10%)
      const switchCost = Math.min(taskSwitches * 6, 50);
      bandwidth -= switchCost;
      if (taskSwitches > 0) {
        residue.push({
          source: 'Context Switches',
          percentage: switchCost,
          time: 'Today',
          description: `${taskSwitches} switches × ~23min recovery each`
        });
      }

      // Focus time impact
      const focusImpact: Record<string, number> = {
        '<15': 25,
        '15-30': 18,
        '30-60': 10,
        '60-90': 5,
        '90+': 0
      };
      const focusCost = focusImpact[longestFocus] || 10;
      bandwidth -= focusCost;
      if (focusCost > 0) {
        residue.push({
          source: 'Shallow Work Mode',
          percentage: focusCost,
          time: 'Pattern',
          description: 'Never reaching deep focus state'
        });
      }

      // Tab overhead
      const tabCost = Math.min(Math.floor(openTabs / 5) * 3, 20);
      bandwidth -= tabCost;
      if (tabCost > 0) {
        residue.push({
          source: 'Background Tabs',
          percentage: tabCost,
          time: 'Ambient',
          description: `${openTabs} tabs consuming attention`
        });
      }

      // Ensure minimum
      bandwidth = Math.max(bandwidth, 5);

      onComplete({ taskSwitches, longestFocus, openTabs }, bandwidth, residue);
    }, 1500);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center mb-12"
      >
        <h2
          className="text-3xl md:text-4xl font-bold text-white mb-4"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Attention Bandwidth Diagnostic
        </h2>
        <p className="text-gray-400">
          Answer 3 questions to calculate your current mental capacity
        </p>
      </motion.div>

      <Question
        index={0}
        question="How many times have you switched tasks in the last hour?"
        subtext="Each switch leaves 'residue' that consumes bandwidth"
      >
        <Slider
          value={taskSwitches}
          onChange={setTaskSwitches}
          min={0}
          max={15}
          labels={['0', '5', '10', '15+']}
        />
        <div className="mt-4 text-center">
          <span className="text-2xl font-bold text-emerald-400">{taskSwitches}</span>
          <span className="text-gray-500 ml-2">switches</span>
        </div>
      </Question>

      <Question
        index={1}
        question="What's your longest uninterrupted stretch today?"
        subtext="Deep work requires 90+ minutes to reach flow state"
      >
        <OptionSelector
          options={focusOptions}
          selected={longestFocus}
          onChange={setLongestFocus}
        />
      </Question>

      <Question
        index={2}
        question="How many browser tabs/apps are open right now?"
        subtext="Each open context consumes background attention"
      >
        <Slider
          value={openTabs}
          onChange={setOpenTabs}
          min={1}
          max={50}
          labels={['1', '15', '30', '50+']}
        />
        <div className="mt-4 text-center">
          <span className="text-2xl font-bold text-emerald-400">{openTabs}</span>
          <span className="text-gray-500 ml-2">open contexts</span>
        </div>
      </Question>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center pt-8"
      >
        <button
          onClick={calculateBandwidth}
          disabled={isCalculating}
          className="px-8 py-4 rounded-xl bg-emerald-500 text-white font-semibold text-lg hover:bg-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
        >
          {isCalculating ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Brain className="w-5 h-5" />
              Calculate My Bandwidth
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
};

// ============================================================================
// RESULTS DISPLAY
// ============================================================================
interface ResultsDisplayProps {
  bandwidth: number;
  residue: ResidueItem[];
  answers: DiagnosticAnswers;
  onReset: () => void;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ bandwidth, residue, answers, onReset }) => {
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    if (bandwidth < 30) {
      soundEngine.playAlert();
    }
    setTimeout(() => setShowTimeline(true), 2000);
  }, [bandwidth]);

  const getStatusMessage = () => {
    if (bandwidth >= 70) return { text: 'Healthy Capacity', icon: CheckCircle, color: '#22c55e' };
    if (bandwidth >= 40) return { text: 'Moderate Depletion', icon: AlertTriangle, color: '#f59e0b' };
    return { text: 'Critical Depletion', icon: AlertTriangle, color: '#ef4444' };
  };

  const status = getStatusMessage();

  // Simulate a typical day timeline
  const timelineEvents = [
    { time: '9:00 AM', event: 'Start of day', bandwidth: 100 },
    { time: '9:15 AM', event: 'Email check', bandwidth: 77, cost: 23 },
    { time: '9:30 AM', event: 'Slack notification', bandwidth: 60, cost: 17 },
    { time: '10:00 AM', event: 'Quick standup', bandwidth: 48, cost: 12 },
    { time: '10:30 AM', event: 'Context switch', bandwidth: 35, cost: 13 },
    { time: '11:00 AM', event: 'Another interrupt', bandwidth: 25, cost: 10 },
    { time: 'NOW', event: 'Current state', bandwidth, cost: 25 - bandwidth },
  ];

  const handleShare = () => {
    const text = `My attention bandwidth is at ${bandwidth}%.\n\nConsumed by:\n${residue.map(r => `• ${r.source}: ${r.percentage}%`).join('\n')}\n\nCalculate yours: `;
    if (navigator.share) {
      navigator.share({ title: 'My Attention Bandwidth', text });
    } else {
      navigator.clipboard.writeText(text);
      alert('Results copied to clipboard!');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Main result */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center mb-12"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
          style={{ background: `${status.color}20`, border: `1px solid ${status.color}40` }}
        >
          <status.icon className="w-5 h-5" style={{ color: status.color }} />
          <span style={{ color: status.color }} className="font-medium">{status.text}</span>
        </motion.div>

        <h2
          className="text-4xl md:text-6xl font-bold mb-4"
          style={{
            color: status.color,
            fontFamily: 'JetBrains Mono, monospace'
          }}
        >
          {bandwidth}%
        </h2>
        <p className="text-gray-400 text-lg">Available Mental Bandwidth</p>
      </motion.div>

      {/* Bandwidth bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-12"
      >
        <BandwidthBar
          percentage={bandwidth}
          showFragments={true}
          residueItems={residue}
        />
      </motion.div>

      {/* Residue breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mb-12"
      >
        <h3
          className="text-xl font-semibold text-white mb-6"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          What's Consuming Your Bandwidth
        </h3>
        <div className="space-y-4">
          {residue.map((item, index) => (
            <motion.div
              key={item.source}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 + index * 0.2 }}
              className="flex items-center gap-4 p-4 rounded-xl bg-gray-800/50 border border-gray-700"
            >
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold"
                style={{
                  background: '#ef444420',
                  color: '#ef4444',
                  fontFamily: 'JetBrains Mono, monospace'
                }}
              >
                {item.percentage}%
              </div>
              <div className="flex-1">
                <div className="font-medium text-white">{item.source}</div>
                <div className="text-sm text-gray-500">{item.description}</div>
              </div>
              <div
                className="text-xs text-gray-500"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {item.time}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Timeline visualization */}
      <AnimatePresence>
        {showTimeline && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h3
              className="text-xl font-semibold text-white mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Your Day So Far
            </h3>
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-700" />

              {timelineEvents.map((event, index) => {
                const color = event.bandwidth >= 70 ? '#22c55e' : event.bandwidth >= 40 ? '#f59e0b' : '#ef4444';
                return (
                  <motion.div
                    key={event.time}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.15 }}
                    className="relative flex items-center gap-6 mb-4"
                  >
                    {/* Timeline dot */}
                    <div
                      className="w-3 h-3 rounded-full z-10"
                      style={{ background: color }}
                    />

                    {/* Content */}
                    <div className="flex-1 flex items-center gap-4 p-3 rounded-lg bg-gray-800/30">
                      <span
                        className="text-sm text-gray-500 w-20"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {event.time}
                      </span>
                      <span className="text-gray-400 flex-1">{event.event}</span>
                      <div className="flex items-center gap-2">
                        {event.cost && (
                          <span className="text-xs text-red-400">-{event.cost}%</span>
                        )}
                        <span
                          className="font-medium"
                          style={{
                            color,
                            fontFamily: 'JetBrains Mono, monospace'
                          }}
                        >
                          {event.bandwidth}%
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="flex flex-wrap justify-center gap-4"
      >
        <button
          onClick={handleShare}
          className="px-6 py-3 rounded-xl bg-gray-800 text-white font-medium hover:bg-gray-700 transition-all flex items-center gap-2"
        >
          <Share2 className="w-5 h-5" />
          Share Results
        </button>
        <button
          onClick={onReset}
          className="px-6 py-3 rounded-xl bg-emerald-500/20 text-emerald-400 font-medium hover:bg-emerald-500/30 transition-all flex items-center gap-2"
        >
          <RefreshCw className="w-5 h-5" />
          Recalculate
        </button>
      </motion.div>
    </div>
  );
};

// ============================================================================
// RECOVERY SECTION
// ============================================================================
const RecoverySection: React.FC = () => {
  const [showSimulation, setShowSimulation] = useState(false);

  const strategies = [
    {
      title: 'Block 90-Minute Focus Sessions',
      description: 'Schedule uninterrupted time blocks. It takes 23 minutes to refocus after each interruption.',
      recovery: 15,
      icon: Clock
    },
    {
      title: 'Batch Similar Tasks',
      description: 'Group emails, messages, and meetings together to reduce context switching.',
      recovery: 20,
      icon: Layers
    },
    {
      title: 'Close Unnecessary Tabs',
      description: 'Each open tab is a potential distraction and consumes background attention.',
      recovery: 10,
      icon: Brain
    }
  ];

  return (
    <div className="py-16 px-6 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Recovery Protocol
          </h2>
          <p className="text-gray-400">
            Evidence-based strategies to recover your attention bandwidth
          </p>
        </motion.div>

        <div className="space-y-6 mb-12">
          {strategies.map((strategy, index) => (
            <motion.div
              key={strategy.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-4 p-6 rounded-xl bg-gray-800/50 border border-gray-700"
            >
              <div className="p-3 rounded-lg bg-emerald-500/20">
                <strategy.icon className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">{strategy.title}</h3>
                <p className="text-gray-400 text-sm">{strategy.description}</p>
              </div>
              <div
                className="text-emerald-400 font-bold"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                +{strategy.recovery}%
              </div>
            </motion.div>
          ))}
        </div>

        {/* Ideal Day Simulation */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <button
            onClick={() => {
              soundEngine.playRecovery();
              setShowSimulation(!showSimulation);
            }}
            className="px-6 py-3 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-all"
          >
            {showSimulation ? 'Hide' : 'Show'} Ideal Day Simulation
          </button>

          <AnimatePresence>
            {showSimulation && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-8 p-6 rounded-xl bg-emerald-900/20 border border-emerald-500/30"
              >
                <h4 className="text-lg font-semibold text-emerald-400 mb-4">
                  With focus protocols applied:
                </h4>
                <div className="space-y-3 text-left">
                  {[
                    { time: '9:00 AM', event: 'Deep work block starts', bandwidth: 100 },
                    { time: '10:30 AM', event: 'First break (planned)', bandwidth: 95 },
                    { time: '10:45 AM', event: 'Batch check messages', bandwidth: 85 },
                    { time: '11:00 AM', event: 'Second deep work block', bandwidth: 85 },
                    { time: '12:30 PM', event: 'Lunch break', bandwidth: 80 },
                    { time: 'END OF DAY', event: 'Maintain high capacity', bandwidth: 75 },
                  ].map((event) => (
                    <div key={event.time} className="flex items-center gap-4 text-sm">
                      <span className="text-emerald-400/60 w-24" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {event.time}
                      </span>
                      <span className="text-gray-300 flex-1">{event.event}</span>
                      <span className="text-emerald-400 font-medium">{event.bandwidth}%</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm text-emerald-400/60 italic">
                  You'd maintain 3x more bandwidth throughout the day.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================================================
// RESEARCH SECTION
// ============================================================================
const ResearchSection: React.FC = () => {
  const findings = [
    { stat: '23 min', description: 'Average time to refocus after an interruption', source: 'Gloria Mark, UC Irvine' },
    { stat: '3 min', description: 'Average time before the typical worker switches tasks', source: 'RescueTime' },
    { stat: '40%', description: 'Increase in stress from fragmented work', source: 'Microsoft Research' },
    { stat: '1 in 5', description: 'Interruptions are actually necessary', source: 'Basex Research' },
  ];

  return (
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            The Research
          </h2>
          <p className="text-gray-400">
            This isn't just a feeling—the data confirms it
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {findings.map((finding, index) => (
            <motion.div
              key={finding.stat}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-xl bg-gray-800/50 border border-gray-700"
            >
              <div
                className="text-3xl font-bold text-emerald-400 mb-2"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {finding.stat}
              </div>
              <p className="text-gray-300 mb-2">{finding.description}</p>
              <p
                className="text-xs text-gray-500"
                style={{ fontFamily: 'Source Serif 4, serif' }}
              >
                — {finding.source}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const AttentionResidueStudy: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'intro' | 'questions' | 'results'>('intro');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [results, setResults] = useState<{
    answers: DiagnosticAnswers;
    bandwidth: number;
    residue: ResidueItem[];
  } | null>(null);

  useEffect(() => {
    soundEngine.setEnabled(soundEnabled);
  }, [soundEnabled]);

  const handleComplete = (answers: DiagnosticAnswers, bandwidth: number, residue: ResidueItem[]) => {
    setResults({ answers, bandwidth, residue });
    setPhase('results');
  };

  const handleReset = () => {
    setResults(null);
    setPhase('questions');
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0d0d0d 100%)',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      <FontLoader />

      {/* Subtle brain-wave pattern */}
      <div
        className="fixed inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 Q 25 0, 50 10 T 100 10' fill='none' stroke='%2322c55e' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 20px'
        }}
      />

      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-8 py-6">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/labs')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Labs</span>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="p-2 rounded-lg bg-gray-800/50 text-gray-400 hover:text-white transition-colors"
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </motion.button>
      </div>

      {/* Main content */}
      <div className="pt-24 pb-12 px-6">
        <AnimatePresence mode="wait">
          {phase === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="min-h-[70vh] flex flex-col items-center justify-center text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mb-8"
              >
                <Brain className="w-20 h-20 text-emerald-400/30 mx-auto" />
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-4xl md:text-6xl font-bold text-white mb-6"
              >
                How Fragmented Is Your{' '}
                <span className="text-emerald-400">Focus</span>{' '}
                Right Now?
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-xl text-gray-400 max-w-xl mb-12"
              >
                Every context switch leaves "residue" that consumes your mental bandwidth.
                Calculate how depleted you really are.
              </motion.p>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                onClick={() => setPhase('questions')}
                className="px-8 py-4 rounded-xl bg-emerald-500 text-white font-semibold text-lg hover:bg-emerald-600 transition-all flex items-center gap-3"
              >
                <Brain className="w-5 h-5" />
                Start Diagnostic
              </motion.button>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-8 text-sm text-gray-500"
              >
                Takes 30 seconds • Based on attention research
              </motion.p>
            </motion.div>
          )}

          {phase === 'questions' && (
            <motion.div
              key="questions"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-12"
            >
              <DiagnosticCalculator onComplete={handleComplete} />
            </motion.div>
          )}

          {phase === 'results' && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-12"
            >
              <ResultsDisplay
                bandwidth={results.bandwidth}
                residue={results.residue}
                answers={results.answers}
                onReset={handleReset}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Additional sections (only show after results) */}
      {phase === 'results' && (
        <>
          <RecoverySection />
          <ResearchSection />

          {/* Footer */}
          <footer className="py-16 px-6 border-t border-gray-800">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-gray-500 text-sm mb-4">
                Based on research by Gloria Mark (UC Irvine), Cal Newport, and Microsoft Research.
              </p>
              <button
                onClick={() => navigate('/labs')}
                className="text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                ← Explore More Labs
              </button>
            </div>
          </footer>
        </>
      )}

      {/* Lab Navigation */}
      <LabNavigation currentLabId="attention-residue" variant="dark" />
    </div>
  );
};

export default AttentionResidueStudy;
